import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  AlertNotification,
  Button,
  colors,
  IconButton,
  LabeledRadio,
  Popup,
  PopupHeader,
  Select,
  SkubotSpinner,
  Text,
  Typography,
  XIcon,
} from "@commonsku/styles";
import { closePopup } from "../../../actions/popup";
import styled from "styled-components";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { BASE_ZINDEX } from "../../../popup-factory";
import { useAccountingCustomers } from "../hooks/useAccountingCustomers";
import { useIdentity } from "../../../hooks";

enum AccountingIntegrationOptions {
  KeepCurrent = 0,
  Unmap = 1,
  Remap = 2,
}
interface AccountingIntegrationPopupProps {
  accountingContactRef: string;
  onUpdate: (data) => void;
}

export interface PopupHeaderProps {
  title: React.ReactNode;
  onPopupClose: () => void;
}

export const PopupBaseProps = {
  style: {
    height: "auto",
    borderRadius: "10px",
  },
  zIndex: BASE_ZINDEX + 201,
  overlayZIndex: BASE_ZINDEX + 101,
};

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 16px;
`;

const AccountIntegrationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors.neutrals[100]};
  font-weight: 400;
`;

const StyledPopupHeader = styled(PopupHeader)`
  font-size: 24px;
  font-weight: 400;
  line-height: 40px;
  color: ${colors.neutrals["90"]};
  margin-bottom: 16px !important;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 30vh;
`;

const ErrorContainer = styled.div`
  height: 10vh;
`;

const PopupButtonFooter = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
`;

const PopupHeaderWithCloseIcon = ({
  title,
  onPopupClose,
}: PopupHeaderProps) => (
  <StyledPopupHeader>
    <Text>{title}</Text>
    <IconButton Icon={XIcon} variant="text" onClick={onPopupClose} />
  </StyledPopupHeader>
);

const LoadingContent = ({ mobile }: { mobile: boolean }) => (
  <LoadingContainer>
    <SkubotSpinner size={mobile ? "medium" : "default"} />
  </LoadingContainer>
);

const ErrorContent = () => (
  <ErrorContainer>
    <AlertNotification alertType="error">
      Unable to retrieve customer details. Please try again, or contact support
      if the problem persists.
    </AlertNotification>
  </ErrorContainer>
);

const AccountingIntegrationPopup = (props: AccountingIntegrationPopupProps) => {
  const { onUpdate, accountingContactRef } = props;
  const [radioOption, setRadioOption] = useState(
    AccountingIntegrationOptions.KeepCurrent
  );
  const [newClientData, setNewClientData] = useState<string | null>(
    accountingContactRef
  );

  const {
    loading,
    error,
    customers,
    loadCustomers,
  } = useAccountingCustomers();
  const currentCustomer = customers.find(customer => customer.id === accountingContactRef);

  const dispatch = useDispatch();
  const isMobile = useMobileScreen();

  const customerSelectOptions = useMemo(
    () => customers.map(
      customer => ({
        value: customer.id,
        label: customer.name
      })
    ),
    [customers]
  );

  const popupStyles = useMemo(() => {
    return {
      ...PopupBaseProps.style,
      width: isMobile ? "80vw" : "50vw",
      minWidth: isMobile ? "250px" : "450px",
    };
  }, [isMobile]);

  const handlePopupClose = () => {
    dispatch(closePopup());
  };

  const handleUpdateClick = () => {
    if (radioOption === AccountingIntegrationOptions.Unmap) {
      onUpdate(false);
    } else if (radioOption === AccountingIntegrationOptions.Remap) {
      onUpdate(newClientData);
    }

    handlePopupClose();
  };

  const handleTryAgainClick = () => {
    loadCustomers();
  };

  if (!!error && !loading && (!currentCustomer || customers.length === 0)) {
    return (
      <Popup
        {...PopupBaseProps}
        style={{ ...popupStyles }}
        header={
          <PopupHeaderWithCloseIcon
            title="Accounting Integration"
            onPopupClose={handlePopupClose}
          />
        }
        onClose={handlePopupClose}
      >
        <StyledContainer>
          <ErrorContent />
          <PopupButtonFooter>
            <Button
              variant="secondary"
              onClick={handlePopupClose}
              size="medium"
            >
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleTryAgainClick}
              size="medium"
            >
              Try Again
            </Button>
      </PopupButtonFooter>
        </StyledContainer>
      </Popup>
    );
  }

  return (
    <Popup
      {...PopupBaseProps}
      style={{ ...popupStyles }}
      header={
        <PopupHeaderWithCloseIcon
          title="Accounting Integration"
          onPopupClose={handlePopupClose}
        />
      }
      onClose={handlePopupClose}
    >
      {loading ? (
        <LoadingContent mobile={isMobile} />
      ) : (
        <>
          <StyledContainer>
            <AccountIntegrationsContainer>
              <Typography.Text style={{ marginTop: "0px" }}>
                The company is currently mapped to{" "}
                {currentCustomer?.name}
              </Typography.Text>
              <LabeledRadio
                label={`Keep current mapping: ${currentCustomer?.name}`}
                value={AccountingIntegrationOptions.KeepCurrent}
                checked={
                  radioOption === AccountingIntegrationOptions.KeepCurrent
                }
                onChange={() =>
                  setRadioOption(AccountingIntegrationOptions.KeepCurrent)
                }
              />
              <LabeledRadio
                label="Unmap"
                value={AccountingIntegrationOptions.Unmap}
                checked={radioOption === AccountingIntegrationOptions.Unmap}
                onChange={() => {
                  setRadioOption(AccountingIntegrationOptions.Unmap);
                  setNewClientData(null);
                }}
              />
              <LabeledRadio
                label="Remap"
                value={AccountingIntegrationOptions.Remap}
                checked={radioOption === AccountingIntegrationOptions.Remap}
                onChange={() => {
                  setRadioOption(AccountingIntegrationOptions.Remap);
                  setNewClientData(null);
                }}
              />
              {radioOption === AccountingIntegrationOptions.Remap && (
                <Select
                  options={customerSelectOptions}
                  onChange={(option) => {
                    setNewClientData(option.value);
                  }}
                  inPopup
                />
              )}
            </AccountIntegrationsContainer>
            <PopupButtonFooter>
              <Button
                variant="secondary"
                onClick={handlePopupClose}
                size="medium"
              >
                Close
              </Button>
              <Button
                disabled={
                  radioOption === AccountingIntegrationOptions.Remap &&
                  !newClientData
                }
                variant="primary"
                onClick={handleUpdateClick}
                size="medium"
              >
                Update
              </Button>
            </PopupButtonFooter>
          </StyledContainer>
        </>
      )}
    </Popup>
  );
};

export default AccountingIntegrationPopup;
